import configRaw from "../../next.config.js";

export const BASE_URL_API =
  process.env.WP_API_URL?.slice(-1) === "/"
    ? `${process.env.WP_API_URL.slice(0, -1)}`
    : process.env.WP_API_URL;

export const getUrl = (lang?: string, defaultLang?: string, v1?: boolean) => {
  if (v1) {
    return lang && defaultLang && lang !== defaultLang
      ? `${BASE_URL_API}/${lang}/wp-json`
      : BASE_URL_V1;
  } else {
    return lang && defaultLang && lang !== defaultLang
      ? `${BASE_URL_API}/${lang}/wp-json/wp/v2`
      : BASE_URL;
  }
};

export const BASE_URL_V1 = `${BASE_URL_API}/wp-json`;
export const BASE_URL_V3 = `${BASE_URL_API}/wp-json/acf/v3`;
export const BASE_URL = `${BASE_URL_API}/wp-json/wp/v2`;

/**
 * Get relative path from link. Please pass also the type property for menu items to enable external links.
 * @param url
 * @param urlType (for menu links only)
 */
export const getRelativePathFromLink = (url: string, urlType = "default") => {
  if (validURL(url) && urlType !== "custom") {
    const u = new URL(url);
    return `${u.pathname}${u.search}${u.hash}`;
  }
  return url;
};

interface getPathNameArrayParams {
  url: string;
  blacklist?: string[];
}
export const getPathNameArray = ({
  url,
  blacklist = [],
}: getPathNameArrayParams) => {
  const path = getRelativePathFromLink(url);
  return path.split("/").filter((el) => el !== "" && !blacklist.includes(el));
};
type StrictConfig = {
  i18n?: { defaultLocale: string };
  trailingSlash?: boolean;
};
const config = configRaw as StrictConfig;
const defaultLocale = config.i18n?.defaultLocale ?? "de";
const trailingSlash = config.trailingSlash ?? true;
export const optimizeInternalUrl = (href: string) => {
  if (href.startsWith(`/${defaultLocale}`)) {
    href = href.replace(`/${defaultLocale}`, "");
  }
  return `${href.replace(/\/$/, "")}${trailingSlash ? "/" : ""}`;
};

function validURL(str: string) {
  try {
    new URL(str);
    // eslint-disable-next-line  unused-imports/no-unused-vars
  } catch (e) {
    return false;
  }
  return true;
}

//TODO: maybe check this only once at startup
const checkPublicURLEnv = () => {
  if (!process.env.NEXT_PUBLIC_PUBLIC_URL) {
    throw new Error(
      "NEXT_PUBLIC_PUBLIC_URL not set but indispensable for isInternalLinkAbsolute",
    );
  }
  if (!validURL(process.env.NEXT_PUBLIC_PUBLIC_URL)) {
    throw new Error(
      "NEXT_PUBLIC_PUBLIC_URL " +
        process.env.NEXT_PUBLIC_PUBLIC_URL +
        " is no valid url",
    );
  }
};

//checks whether an link is internal and absolute
export const isInternalLinkAbsolute = (url: string) => {
  checkPublicURLEnv();
  return !!(
    validURL(url) &&
    (new URL(url).hostname ===
      new URL(process.env.NEXT_PUBLIC_PUBLIC_URL!).hostname ||
      (process.env.NEXT_PUBLIC_INTERNAL_ORIGINS &&
        process.env.NEXT_PUBLIC_INTERNAL_ORIGINS?.split(",")?.some?.(
          (internalHost) => {
            return (
              validURL(internalHost) &&
              new URL(url).hostname === new URL(internalHost).hostname
            );
          },
        )))
  );
};

/**
 * is intended to optimize all kinds of urls
 * currently does following:
 * - internal absolute urls are converted to relative ones
 * - trailing slash is applied or removed according to next.config.js to prevent 3xx errors
 *
 * @param url url to optimize
 * @returns optimized url
 */
export const optimizeURL = (url: string) => {
  if (!url) return;
  if (url === "/") return url;
  if (url.startsWith("/#")) {
    return url;
  }
  if (url.startsWith("#")) {
    return "/" + url;
  }
  if (isInternalLinkAbsolute(url)) {
    const relativeURL = getRelativePathFromLink(url);
    return optimizeInternalUrl(relativeURL);
  }
  if (!validURL(url)) {
    //assume relative url
    return optimizeInternalUrl(url);
  }
  return url;
};

export const setTrailingSlash = (url: string) => {
  return `${url.replace(/\/$/, "")}${config.trailingSlash ? "/" : ""}`;
};

/**
 * Builds an internal absolute link from a relative or absolute (independent which origin) url
 * @param url url (relative or absolute) to be transformed to an absolute internal link
 */
export const buildAbsoluteInternalLink = (url: string) => {
  checkPublicURLEnv();
  const relativeURL = getRelativePathFromLink(url);
  if (!relativeURL.startsWith("/")) {
    throw new Error(
      "can't build absolute internal link from relative url which does not start with /",
    );
  }
  return setTrailingSlash(
    `${process.env.NEXT_PUBLIC_PUBLIC_URL}${relativeURL}`,
  );
};

// Check if frontend slug is valid before passing it to the backend to prevent DOS attacks
export function isValidWpSlug(slug: string): boolean {
  // Slug must be a non-empty string, and only contain lowercase letters, numbers, and hyphens
  const wpSlugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

  // Trim whitespace from the slug (although WP slugs shouldn't have leading/trailing spaces)
  const trimmedSlug = slug?.trim();

  // Return true if the slug matches the pattern
  return wpSlugPattern.test(trimmedSlug);
}

export const getServiceURL = (locale?: string) => {
  return locale === "fr"
    ? process.env.SERVICE_API_URL_FR
    : process.env.SERVICE_API_URL;
};
